import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const MAX_RECENTS = 50;

export type RecentEntry = {
  id: string;
  playedAt: number;
};

type RecentsState = {
  recentEntries: RecentEntry[];
  addRecent: (id: string) => void;
  removeRecent: (id: string) => void;
  clearRecents: () => void;
};

export const useRecentsStore = create<RecentsState>()(
  persist(
    (set) => ({
      recentEntries: [],

      addRecent: (id) =>
        set((state) => {
          // Move-to-front: remove any existing entry for this id,
          // then add a fresh one with the new timestamp at the top.
          const filtered = state.recentEntries.filter((e) => e.id !== id);
          const updated = [{ id, playedAt: Date.now() }, ...filtered].slice(0, MAX_RECENTS);
          return { recentEntries: updated };
        }),

      removeRecent: (id) =>
        set((state) => ({
          recentEntries: state.recentEntries.filter((e) => e.id !== id),
        })),

      clearRecents: () => set({ recentEntries: [] }),
    }),
    {
      name: 'streampitch:recents:v1',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Human-friendly relative-time formatter used in the Recent page.
 * Returns a short label: "just now", "5m ago", "3h ago", "2d ago",
 * or a localized date for older entries.
 */
export function formatTimeAgo(timestamp: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}
